import { OCCURRENCE_SEVERITY_LABELS, OCCURRENCE_TYPE_LABELS } from '@hue/contracts';
import { Button, Input, StatusText, useSnackbar, IconButton } from '@hue/design-system';
import { HeartPulse, Plus, Archive } from 'lucide-react';
import { useState } from 'react';
import { getApiError, recordsApi, type ClientChart } from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import { RequestAlert } from '../../app/data-view.js';
import { SectionCardHeader } from '../../app/section-card-header.js';
import { ThemeTag } from './shared.js';
import { themeOf } from './helpers.js';

/// O Quadro de saúde (ADR 0038, decisão 8): o que qualquer ficha vigente declarou, em qualquer
/// serviço, junto dos alertas da equipe e das intercorrências das sessões. Basta uma ficha
/// declarar para aparecer.
///
/// Na forma compacta (modal da lista) ele só lê; na página ele também cria e encerra alertas.
export function HealthPanel({
  chart,
  timeZone,
  compact = false,
  canManage = false,
  onOpenRecord,
  onChanged,
}: {
  chart: ClientChart;
  timeZone: string;
  compact?: boolean;
  canManage?: boolean;
  onOpenRecord?: (templateId: string, edit: boolean) => void;
  onChanged?: () => Promise<void>;
}) {
  const { health, themes } = chart;
  const manage = canManage && !compact;
  const count = health.points;
  const byTheme = themes
    .map((theme) => ({
      theme,
      items: health.declarations.filter((item) => item.themeKey === theme.key),
    }))
    .filter((group) => group.items.length);
  const alerts = chart.alerts.filter((alert) => !alert.closedAt);

  return (
    <aside
      aria-label="Quadro de saúde"
      className="rounded-2xl border border-surface-border bg-surface-card shadow-soft"
    >
      <SectionCardHeader title="Quadro de saúde" icon={HeartPulse} iconClassName="text-danger">
        <span className="text-xs text-content-muted">
          {count} {count === 1 ? 'ponto' : 'pontos'}
          {health.divergences.length ? (
            <span className="text-warning">
              {' '}
              · {health.divergences.length}{' '}
              {health.divergences.length === 1 ? 'divergência' : 'divergências'}
            </span>
          ) : null}
        </span>
      </SectionCardHeader>
      <div className="grid gap-4 px-4 py-4">
        {health.divergences.map((divergence) => {
          const deniers = [
            ...new Map(divergence.denied.map((item) => [item.templateId, item])).values(),
          ];
          return (
            <div
              key={divergence.themeKey}
              className="rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm"
            >
              <p className="flex flex-wrap items-center gap-2 font-semibold">
                <ThemeTag theme={themeOf(themes, divergence.themeKey)} />
                Divergências
              </p>
              <ul className="mt-1.5 grid gap-1 text-xs leading-5 text-content-secondary">
                {[
                  ...divergence.declared.map((item) => ({ ...item, answer: item.value })),
                  ...divergence.denied.map((item) => ({ ...item, answer: 'Não' })),
                ].map((item) => (
                  <li key={`${item.templateId}-${item.questionId}`}>
                    <strong>
                      {item.templateName} v{item.version}
                    </strong>{' '}
                    ({formatDate(item.recordedAt, timeZone)}) · {item.questionLabel}{' '}
                    <strong>{item.answer}</strong>
                  </li>
                ))}
              </ul>
              {canManage && onOpenRecord
                ? deniers.map((item) => (
                    <button
                      key={item.templateId}
                      type="button"
                      onClick={() => onOpenRecord(item.templateId, true)}
                      className="mt-2 block cursor-pointer text-left text-xs font-semibold text-brand-400 hover:underline"
                    >
                      Confirmar com o cliente e atualizar {item.templateName}
                    </button>
                  ))
                : null}
            </div>
          );
        })}

        <section className="grid gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-content-faint">
            Declarado pelo cliente
          </h3>
          {byTheme.length ? (
            byTheme.flatMap(({ theme, items }) =>
              items.map((item) => (
                <div key={`${item.templateId}-${item.questionId}`} className="grid gap-1 text-sm">
                  <p className="flex flex-wrap items-center gap-2">
                    <ThemeTag theme={theme} />
                    <span className="font-semibold">{item.value}</span>
                  </p>
                  <p className="text-xs text-content-muted">
                    {item.questionLabel} ·{' '}
                    {onOpenRecord ? (
                      <button
                        type="button"
                        onClick={() => onOpenRecord(item.templateId, false)}
                        className="cursor-pointer font-semibold text-brand-400 hover:underline"
                      >
                        {item.templateName} v{item.version}
                      </button>
                    ) : (
                      `${item.templateName} v${String(item.version)}`
                    )}{' '}
                    · {formatDate(item.recordedAt, timeZone)}
                  </p>
                </div>
              )),
            )
          ) : (
            <p className="text-xs text-content-faint">Nada declarado em nenhuma ficha.</p>
          )}
        </section>

        <AlertsSection
          clientId={chart.client.id}
          alerts={alerts}
          timeZone={timeZone}
          manage={manage}
          onChanged={onChanged}
        />

        <section className="grid gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-content-faint">
            Intercorrências
          </h3>
          {health.occurrences.length ? (
            health.occurrences.map((occurrence) => (
              <div key={occurrence.appointmentId} className="grid gap-1 text-sm">
                <p className="flex flex-wrap items-center gap-2">
                  <StatusText tone="danger">{OCCURRENCE_TYPE_LABELS[occurrence.type]}</StatusText>
                  <span className="font-semibold">
                    {OCCURRENCE_SEVERITY_LABELS[occurrence.severity]} ·{' '}
                    {occurrence.serviceDisplayName}
                  </span>
                </p>
                <p className="text-xs leading-5 text-content-secondary">{occurrence.description}</p>
                <p className="text-xs text-content-muted">
                  {occurrence.products.length
                    ? `${occurrence.products.map((product) => product.displayName).join(', ')} · `
                    : ''}
                  {formatDate(occurrence.recordedAt, timeZone)} · {occurrence.recordedByDisplayName}
                </p>
              </div>
            ))
          ) : (
            <p className="text-xs text-content-faint">Nenhuma intercorrência registrada.</p>
          )}
        </section>

        {compact ? null : (
          <p className="text-xs leading-5 text-content-faint">
            Junta todas as fichas e sessões, de qualquer serviço. Basta uma ficha declarar para
            aparecer.
          </p>
        )}
      </div>
    </aside>
  );
}

function AlertsSection({
  clientId,
  alerts,
  timeZone,
  manage,
  onChanged,
}: {
  clientId: string;
  alerts: ClientChart['alerts'];
  timeZone: string;
  manage: boolean;
  onChanged: (() => Promise<void>) | undefined;
}) {
  const snackbar = useSnackbar();
  const [creating, setCreating] = useState(false);
  const [text, setText] = useState('');
  const [closing, setClosing] = useState<string>();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>, message: string) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
      await onChanged?.();
      setCreating(false);
      setClosing(undefined);
      setText('');
      setReason('');
      snackbar.success(message);
    } catch (failure) {
      setError(getApiError(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="grid gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-content-faint">
          Alertas da equipe
        </h3>
        {manage && !creating ? (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-brand-400 hover:underline"
          >
            <Plus className="size-3.5" aria-hidden="true" />
            Novo alerta
          </button>
        ) : null}
      </div>
      {creating ? (
        <form
          className="grid gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void run(() => recordsApi.createAlert(clientId, text), 'Alerta criado.');
          }}
        >
          <Input
            label="Alerta"
            value={text}
            maxLength={500}
            placeholder="Ex.: Usar só produtos sem amônia."
            onChange={(event) => setText(event.target.value)}
          />
          {error ? <RequestAlert message={error} /> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setCreating(false)}>
              Cancelar
            </Button>
            <Button type="submit" size="sm" loading={busy}>
              Criar alerta
            </Button>
          </div>
        </form>
      ) : null}
      {alerts.length ? (
        alerts.map((alert) => (
          <div key={alert.id} className="grid gap-1 text-sm">
            <p className="flex flex-wrap items-center gap-2">
              <StatusText tone="warning">Alerta</StatusText>
              <span>{alert.text}</span>
            </p>
            <p className="text-xs text-content-muted">
              {alert.createdByDisplayName} · {formatDate(alert.createdAt, timeZone)}
            </p>
            {closing === alert.id ? (
              <form
                className="mt-1 grid gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  void run(() => recordsApi.closeAlert(alert.id, reason), 'Alerta encerrado.');
                }}
              >
                <Input
                  label="Por que encerrar?"
                  value={reason}
                  maxLength={300}
                  placeholder="Ex.: teste de mecha feito, sem reação."
                  onChange={(event) => setReason(event.target.value)}
                />
                {error ? <RequestAlert message={error} /> : null}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setClosing(undefined)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" size="sm" loading={busy}>
                    Encerrar alerta
                  </Button>
                </div>
              </form>
            ) : manage ? (
              <IconButton
                label="Encerrar com motivo"
                icon={<Archive />}
                tone="danger"
                onClick={() => {
                  setError(undefined);
                  setClosing(alert.id);
                }}
              />
            ) : null}
          </div>
        ))
      ) : creating ? null : (
        <p className="text-xs text-content-faint">Nenhum alerta ativo.</p>
      )}
    </section>
  );
}
